<script setup>
import { computed, onMounted, ref, watch, defineProps } from 'vue';
import { useMeta } from 'vue-meta';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import authHeader from '@/services/auth-header';
import { useAuthStore } from '@/stores/auth';
import { useAppStore } from '@/stores/app';
import HintComponent from '@/components/SpecialComponents/HintComponent.vue';
import CalendarUserModal from "@/components/SpecialComponents/CalendarUserModal.vue";

const props = defineProps({
  specialistId: {
    type: [String, Number],
    default: null,
  },
});

useMeta({ title: 'Kalendarz specjalisty' });

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const appStore = useAppStore();

const currentUser = computed(() => authStore.user);

const isLoading = ref(false);
const services = ref([]);
const calendarItems = ref([]);
const datesViewSelect = ref('month');
const viewDate = ref('');
const viewMonth = ref('');
const daysInMonth = ref([]);
const todaysDay = ref('');
const loadError = ref(null);

const monthDayLabels = ['pon.', 'wt.', 'śr.', 'czw.', 'pt.', 'sob.', 'niedz.'];

const selectedSpecialistId = computed(() => {
  return props.specialistId
      || route.params.specialistId
      || route.params.employeeId
      || route.query.specialistId
      || route.query.employeeId
      || null;
});

const filteredServices = computed(() => {
  if (!selectedSpecialistId.value) {
    return services.value;
  }

  return services.value.filter(service => {
    return String(service.employee_id) === String(selectedSpecialistId.value);
  });
});

const serviceNameById = computed(() => {
  return filteredServices.value.reduce((acc, service) => {
    acc[String(service.id)] = service.name;
    return acc;
  }, {});
});

const visibleDates = computed(() => {
  if (datesViewSelect.value === 'sevenDays') {
    return Array.from({ length: 7 }, (_, index) => formatLocalDate(addDays(new Date(), index)));
  }

  if (datesViewSelect.value === 'date' && viewDate.value) {
    return [viewDate.value];
  }

  return [formatLocalDate(new Date())];
});

const hours = computed(() => {
  const uniqueHours = new Set();

  calendarItems.value.forEach(item => {
    if (visibleDates.value.includes(item.date) && item.time) {
      uniqueHours.add(item.time);
    }
  });

  return [...uniqueHours].sort(compareHours);
});

const hasTableData = computed(() => {
  return datesViewSelect.value !== 'month' && visibleDates.value.length > 0;
});

const monthTitle = computed(() => {
  if (!viewMonth.value) {
    return 'Podgląd terminów';
  }

  return `Podgląd terminów: ${viewMonth.value}`;
});

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const addDays = (date, daysToAdd) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + daysToAdd);

  return nextDate;
};

const compareHours = (hourA, hourB) => {
  const [hoursA, minutesA] = hourA.split(':').map(Number);
  const [hoursB, minutesB] = hourB.split(':').map(Number);

  if (hoursA === hoursB) {
    return minutesA - minutesB;
  }

  return hoursA - hoursB;
};

const normalizeService = (serviceWrapper) => {
  return serviceWrapper.service || serviceWrapper;
};

const normalizeCalendarItem = (item, service) => {
  return {
    ...item,
    service_id: item.service_id || service.id,
    service_name: service.name,
  };
};

const getMonthMatrix = (monthValue) => {
  const [yearValue, monthNumber] = monthValue.split('-').map(Number);
  const monthIndex = monthNumber - 1;
  const firstDay = new Date(yearValue, monthIndex, 1);
  const daysInThisMonth = new Date(yearValue, monthIndex + 1, 0).getDate();

  let mondayFirstOffset = firstDay.getDay() - 1;

  if (mondayFirstOffset < 0) {
    mondayFirstOffset = 6;
  }

  const flatDays = [];

  for (let index = 0; index < mondayFirstOffset; index++) {
    flatDays.push('');
  }

  for (let day = 1; day <= daysInThisMonth; day++) {
    flatDays.push(`${yearValue}-${String(monthNumber).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
  }

  while (flatDays.length % 7 !== 0) {
    flatDays.push('');
  }

  const matrix = [];

  for (let index = 0; index < flatDays.length; index += 7) {
    matrix.push(flatDays.slice(index, index + 7));
  }

  return matrix;
};

const getItemsForCell = (date, time) => {
  return calendarItems.value.filter(item => item.date === date && item.time === time);
};

const getItemsForDay = (date) => {
  return calendarItems.value
      .filter(item => item.date === date)
      .sort((itemA, itemB) => compareHours(itemA.time, itemB.time));
};

const isReserved = (item) => {
  return Boolean(item.user_id);
};

const hasPaymentData = (item) => {
  return Boolean(item.payment_url && item.payment_price);
};

const getItemServiceName = (item) => {
  return item.service_name || serviceNameById.value[String(item.service_id)] || 'Usługa';
};

const goToVisitDetails = (item) => {
  if (!isReserved(item)) {
    return;
  }

  router.push({
    path: route.path,
    query: {
      ...route.query,
      userid: item.user_id,
      petid: item.pet_id,
      item_id: item.id,
      serviceId: item.service_id,
      pd: hasPaymentData(item),
    },
  });
};

const setView = () => {
  if (datesViewSelect.value === 'month' && viewMonth.value) {
    daysInMonth.value = getMonthMatrix(viewMonth.value);
    return;
  }

  daysInMonth.value = [];
};

const getServices = async () => {
  const response = await axios.get(`/UserService/${currentUser.value.id}`, authHeader());
  services.value = (response.data || []).map(normalizeService);
};

const getCalendarItemsForServices = async () => {
  const requests = filteredServices.value.map(async service => {
    try {
      const response = await axios
          .get(`/CalendarItemsForServiceInBusinessCalendar/${service.id}`, authHeader());
      return (response.data || []).map(item => normalizeCalendarItem(item, service));
    } catch {
      return [];
    }
  });

  const results = await Promise.all(requests);

  calendarItems.value = results
      .flat()
      .sort((itemA, itemB) => {
        if (itemA.date === itemB.date) {
          return compareHours(itemA.time, itemB.time);
        }

        return itemA.date.localeCompare(itemB.date);
      });
};

const loadCalendar = async () => {
  isLoading.value = true;
  loadError.value = null;

  try {
    await getServices();
    await getCalendarItemsForServices();
    setView();
  } catch (error) {
    loadError.value = 'Nie udało się pobrać kalendarza specjalisty.';
    calendarItems.value = [];
    appStore.setIsAlert('Nie udało się pobrać kalendarza specjalisty');
  } finally {
    isLoading.value = false;
  }
};

watch([datesViewSelect, viewDate, viewMonth], () => {
  setView();
});

watch(selectedSpecialistId, () => {
  if (currentUser.value && services.value.length > 0) {
    getCalendarItemsForServices();
  }
});

onMounted(async () => {
  viewMonth.value = formatLocalDate(new Date()).slice(0, 7);
  todaysDay.value = formatLocalDate(new Date());

  if (!currentUser.value || (currentUser.value.role_id !== 2 && currentUser.value.role_id !== 5)) {
    await router.push('/login');
    return;
  }

  if (currentUser.value.role_id === 2 && !appStore.isSubscribed) {
    await router.push('/dla-biznesu/profile/user-data');
    return;
  }

  await loadCalendar();
});
</script>

<template>
  <div v-if="currentUser" class="specialist-calendar-page">
    <CalendarUserModal />
    <div class="page-header">
      <h1>Kalendarz specjalisty</h1>
      <p class="page-subtitle">
        Podgląd wszystkich terminów i wizyt specjalisty z uwzględnieniem każdej przypisanej usługi.
      </p>
    </div>

    <div class="section-card position-relative">
      <HintComponent page-key="business_panel_calendar_visits_preview" :absolute="true" />

      <div class="view-controls">
        <select v-model="datesViewSelect" name="datesViewSelect" id="datesViewSelect">
          <option value="day">dzisiaj</option>
          <option value="sevenDays">7 dni do przodu</option>
          <option value="month">miesiąc</option>
          <option value="date">konkretna data</option>
        </select>

        <input
            v-if="datesViewSelect === 'date'"
            v-model="viewDate"
            type="date"
            name="viewDate"
            id="viewDate"
        >

        <input
            v-if="datesViewSelect === 'month'"
            v-model="viewMonth"
            type="month"
            name="viewMonth"
            id="viewMonth"
        >

        <button class="btn btn-neutral" type="button" :disabled="isLoading" @click="loadCalendar">
          Odśwież
        </button>
      </div>

      <div class="calendar-header">
        <h2 class="calendar-month-year">{{ monthTitle }}</h2>
      </div>

      <div v-if="isLoading" class="empty-state">
        Ładowanie kalendarza...
      </div>

      <div v-else-if="loadError" class="empty-state empty-state--error">
        {{ loadError }}
      </div>

      <div v-else-if="filteredServices.length === 0" class="empty-state">
        Brak usług przypisanych do wybranego specjalisty.
      </div>

      <div v-else class="calendar-table-wrap">
        <table v-if="hasTableData" class="calendar-table">
          <thead>
          <tr>
            <th>godzina</th>
            <th v-for="date in visibleDates" :key="date">
              {{ date }}
            </th>
          </tr>
          </thead>

          <tbody>
          <tr v-if="hours.length === 0">
            <td :colspan="visibleDates.length + 1" class="empty-cell">
              Brak terminów w wybranym przedziale.
            </td>
          </tr>

          <tr v-for="hour in hours" :key="hour">
            <th>{{ hour }}</th>
            <td v-for="date in visibleDates" :key="`${date}-${hour}`">
              <button
                  v-for="item in getItemsForCell(date, hour)"
                  :key="item.id"
                  type="button"
                  class="calendar-item"
                  :class="{
                    'calendar-item--reserved': isReserved(item),
                    'calendar-item--active': !isReserved(item)
                  }"
                  :disabled="!isReserved(item)"
                  @click="goToVisitDetails(item)"
              >
                <span class="calendar-item__time">{{ item.time }}</span>
                <span class="calendar-item__service">{{ getItemServiceName(item) }}</span>
                <span class="calendar-item__status">
                  {{ isReserved(item) ? 'zarezerwowany' : 'aktywny' }}
                </span>
              </button>
            </td>
          </tr>
          </tbody>
        </table>

        <table v-else class="calendar-table month-view">
          <thead>
          <tr>
            <th v-for="label in monthDayLabels" :key="label">
              {{ label }}
            </th>
          </tr>
          </thead>

          <tbody>
          <tr v-for="(week, weekIndex) in daysInMonth" :key="`week-${weekIndex}`">
            <td
                v-for="(day, dayIndex) in week"
                :key="`day-${weekIndex}-${dayIndex}`"
                :class="{ 'today-cell': day && todaysDay === day }"
            >
              <template v-if="day">
                <strong class="day-label">{{ day }}</strong>

                <div v-if="getItemsForDay(day).length > 0" class="month-items">
                  <button
                      v-for="item in getItemsForDay(day)"
                      :key="item.id"
                      type="button"
                      class="month-item"
                      :class="{
                        'month-item--reserved': isReserved(item),
                        'month-item--active': !isReserved(item)
                      }"
                      :disabled="!isReserved(item)"
                      @click="goToVisitDetails(item)"
                  >
                    <span>{{ item.time }}</span>
                    <span>{{ getItemServiceName(item) }}</span>
                  </button>
                </div>
              </template>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <span class="calendar-info">Przesuń kalendarz w prawo lub lewo, aby zobaczyć więcej dni.</span>
    </div>

    <div class="section-card">
      <div class="legend">
        <span class="legend-item legend-item--active">
          Termin aktywny — klient może się na niego zapisać.
        </span>
        <span class="legend-item legend-item--reserved">
          Termin zarezerwowany — kliknij, aby przejść do szczegółów wizyty.
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.specialist-calendar-page {
  max-width: 1200px;
  margin: 0 auto;
  padding-bottom: 24px;
  box-sizing: border-box;
  font-family: sans-serif;
  color: #1f2937;
}

.page-header {
  margin-bottom: 20px;
  padding-bottom: 14px;
  border-bottom: 2px solid #e5e7eb;
}

.page-header h1 {
  margin: 0;
  font-size: 2.8rem;
  font-weight: 700;
}

.page-subtitle {
  margin: 8px 0 0;
  color: #6b7280;
  font-size: 1.4rem;
}

.section-card {
  width: 100%;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, .08);
  padding: 16px;
  margin-bottom: 16px;
  box-sizing: border-box;
}

.view-controls {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.view-controls select,
.view-controls input {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 1.4rem;
}

.btn {
  border: none;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 1.4rem;
  cursor: pointer;
  transition: all .2s ease-in-out;
}

.btn:disabled {
  opacity: .6;
  cursor: not-allowed;
}

.btn-neutral {
  background: #e5e7eb;
  color: #1f2937;
}

.btn-neutral:hover:not(:disabled) {
  background: #d1d5db;
}

.calendar-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f0f0f0;
}

.calendar-month-year {
  margin: 0;
  font-size: 2rem;
  font-weight: 600;
  color: #333;
}

.calendar-table-wrap {
  width: 100%;
  overflow: auto;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}

.calendar-table {
  width: 100%;
  min-width: 1100px;
  border-collapse: collapse;
  background: #fff;
}

.calendar-table th,
.calendar-table td {
  border: 1px solid #e5e7eb;
  padding: 6px 8px;
  font-size: 1.3rem;
  min-width: 130px;
  text-align: center;
  vertical-align: top;
}

.calendar-table th {
  background: #f9fafb;
  font-weight: 700;
}

.empty-cell {
  padding: 24px !important;
  color: #9ca3af;
}

.calendar-item,
.month-item {
  width: 100%;
  border: none;
  border-radius: 8px;
  padding: 8px;
  margin-bottom: 6px;
  color: #fff;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.calendar-item--active,
.month-item--active {
  background: #5ea01a;
  cursor: default;
}

.calendar-item--reserved,
.month-item--reserved {
  background: #f2790f;
  cursor: pointer;
}

.calendar-item--reserved:hover,
.month-item--reserved:hover {
  background: #d66a0a;
}

.calendar-item__time {
  font-weight: 700;
}

.calendar-item__service,
.calendar-item__status {
  font-size: 1.15rem;
}

.month-view td {
  min-width: 160px;
  height: 110px;
  text-align: left;
}

.today-cell {
  outline: 2px solid #f2790f;
  outline-offset: -2px;
}

.day-label {
  display: block;
  margin-bottom: 8px;
  font-size: 1.2rem;
}

.month-items {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.month-item {
  font-size: 1.15rem;
}

.calendar-info {
  display: none;
  font-size: 12px;
  color: #666;
  margin-top: 10px;
  text-align: right;
}

.legend {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.legend-item {
  font-size: 1.4rem;
  font-weight: bold;
}

.legend-item--active {
  color: #5ea01a;
}

.legend-item--reserved {
  color: #f2790f;
}

.empty-state {
  text-align: center;
  color: #9ca3af;
  font-size: 1.5rem;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
  padding: 24px;
}

.empty-state--error {
  color: #bb2d3b;
  border-color: #fecaca;
  background: #fff1f2;
}

@media (max-width: 768px) {
  .page-header h1 {
    font-size: 2.2rem;
  }

  .calendar-month-year {
    font-size: 1.8rem;
  }

  .calendar-info {
    display: block;
  }

  .btn {
    width: 100%;
  }
}
</style>